import { create } from 'zustand'
import { supabase } from '../lib/supabase'
import { useAuthStore } from './authStore'
import { sendSupportTicketEmail } from '../utils/emailService'

// Support tickets — mirrors src/stores/supabaseCardsStore.ts.
// RLS does the real work: a trainer's select only ever returns their own rows,
// an admin's returns every row. The client-side checks below are convenience
// only (nicer error messages), never the actual guard.

export type TicketStatus = 'open' | 'in_progress' | 'done'

/** Kategorie-Keys. Müssen exakt den Zeilen in support_categories entsprechen (FK). */
export type TicketCategory =
  | 'junioren' | 'bus' | 'material' | 'aktive' | 'spiko' | 'js' | 'technik' | 'it'

export interface SupportTicket {
  id: string
  title: string
  body: string
  status: TicketStatus
  owner_id: string
  /** null bei Altbestand und bei Clients mit veraltetem Service Worker. */
  category: string | null
  created_at: string
  updated_at: string
}

/**
 * Fields a caller is allowed to change. Deliberately narrow — no owner_id,
 * and no category: die Kategorie entscheidet, wohin die Meldung geht, und wird
 * beim Erfassen einmal festgelegt.
 */
export interface TicketPatch {
  title?: string
  body?: string
  status?: TicketStatus
}

interface TicketsStore {
  tickets: SupportTicket[]
  loading: boolean
  error: string | null
  fetchTickets: () => Promise<void>
  addTicket: (
    title: string,
    body: string,
    category: TicketCategory,
  ) => Promise<{ error?: string; mailWarning?: string }>
  updateTicket: (id: string, patch: TicketPatch) => Promise<{ error?: string }>
  deleteTicket: (id: string) => Promise<{ error?: string }>
}

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Offen',
  in_progress: 'In Arbeit',
  done: 'Erledigt',
}

/**
 * Kategorie-Beschriftungen. Bewusst hier als Konstante und nicht aus
 * support_categories geladen: die Tabelle enthält auch die Zieladressen, und
 * die haben im Browser nichts zu suchen. Die Reihenfolge entspricht
 * sort_order in Migration 007.
 *
 * Die Schlüssel müssen zeichengenau mit support_categories.key übereinstimmen —
 * ein Tippfehler schlägt beim Erfassen als Fremdschlüsselfehler auf.
 */
export const CATEGORY_LABELS: Record<TicketCategory, string> = {
  junioren: 'Junioren',
  bus: 'Vereinsbus',
  material: 'Materialbeschaffung',
  aktive: 'Sportchef Aktive',
  spiko: 'Spiko',
  js: 'J+S',
  technik: 'Unterhalt',
  // Rückmeldungen zur App selbst — geht nicht an den Verein, sondern an die
  // Entwicklung. Steht deshalb am Ende der Liste.
  it: 'IT Support App',
}

export const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS) as TicketCategory[]

/** Anzeige-Label für ein gespeichertes Ticket, inkl. Altbestand ohne Kategorie. */
export function categoryLabel(key: string | null): string {
  if (!key) return 'Ohne Kategorie'
  return CATEGORY_LABELS[key as TicketCategory] ?? key
}

export const useSupportTicketsStore = create<TicketsStore>((set, get) => ({
  tickets: [],
  loading: false,
  error: null,

  fetchTickets: async () => {
    set({ loading: true, error: null })
    try {
      // No client-side owner filter: RLS already scopes this per role.
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      set({ tickets: (data as SupportTicket[]) || [], loading: false })
    } catch (error: any) {
      set({ error: error.message, loading: false })
    }
  },

  addTicket: async (title, body, category) => {
    const { user } = useAuthStore.getState()
    if (!user) return { error: 'Nicht angemeldet' }
    const cleanTitle = title.trim()
    if (!cleanTitle) return { error: 'Titel fehlt' }
    if (!category) return { error: 'Bitte eine Kategorie auswählen' }
    try {
      // .select('id') — die ID wird für die Benachrichtigung gebraucht.
      // Bewusst nur die ID und nicht '*': die Antwort soll nicht mehr
      // zurückgeben als nötig.
      const { data, error } = await supabase
        .from('support_tickets')
        .insert({ title: cleanTitle, body: body.trim(), owner_id: user.id, status: 'open', category })
        .select('id')
        .single()
      if (error) throw error

      await get().fetchTickets()

      // Benachrichtigung an die Kategorie. Gleiches Muster wie Bestellungen und
      // Bus-Reservationen: der Datensatz steht bereits, ein Fehler hier darf
      // das Erfassen nicht rückgängig machen — er wird aber, anders als dort,
      // an die Oberfläche gemeldet statt nur in die Konsole geschrieben.
      const mail = await sendSupportTicketEmail(data.id)
      if (!mail.success) {
        console.error('Support-Benachrichtigung fehlgeschlagen:', mail.error)
        return {
          mailWarning:
            'Ticket erfasst — die Benachrichtigung an die Kategorie konnte aber nicht gesendet werden.',
        }
      }
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  updateTicket: async (id, patch) => {
    const updates: TicketPatch = {}
    if (patch.title !== undefined) updates.title = patch.title.trim()
    if (patch.body !== undefined) updates.body = patch.body.trim()
    if (patch.status !== undefined) updates.status = patch.status
    if (Object.keys(updates).length === 0) return {}
    if (updates.title !== undefined && !updates.title) return { error: 'Titel darf nicht leer sein' }
    try {
      // .select() so a row blocked by RLS comes back as 0 rows instead of a
      // silent success (PostgREST reports a filtered-out update as OK).
      const { data, error } = await supabase
        .from('support_tickets')
        .update(updates)
        .eq('id', id)
        .select()
      if (error) throw error
      if (!data || data.length === 0) return { error: 'Keine Berechtigung für dieses Ticket.' }
      await get().fetchTickets()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },

  deleteTicket: async (id) => {
    const { profile } = useAuthStore.getState()
    if (!profile || profile.role !== 'admin') {
      return { error: 'Nur Administratoren können Tickets löschen' }
    }
    try {
      const { error } = await supabase.from('support_tickets').delete().eq('id', id)
      if (error) throw error
      await get().fetchTickets()
      return {}
    } catch (error: any) {
      return { error: error.message }
    }
  },
}))
